/**
 * ASCII Art Generator - 图像处理算法
 *
 * 包含灰度映射、亮度采样、对比度调整等基础算法
 */

import type { RGB, SampledPixel } from '../types'

// ============================================
// 灰度计算
// ============================================

/**
 * 计算单个像素的灰度值
 * 使用 ITU-R BT.601 标准加权公式
 * @param r 红色分量 (0-255)
 * @param g 绿色分量 (0-255)
 * @param b 蓝色分量 (0-255)
 * @returns 灰度值 (0-255)
 */
export function rgbToGrayscale(r: number, g: number, b: number): number {
  // ITU-R BT.601 标准: 0.299R + 0.587G + 0.114B
  return Math.round(0.299 * r + 0.587 * g + 0.114 * b)
}

/**
 * 从RGBA数组计算灰度值
 * @param data Uint8ClampedArray (RGBA格式)
 * @param index 起始索引
 */
export function getGrayscaleFromRGBA(data: Uint8ClampedArray, index: number): number {
  return rgbToGrayscale(data[index], data[index + 1], data[index + 2])
}

/**
 * 计算整个图像的平均灰度
 * @param imageData ImageData对象
 */
export function getAverageGrayscale(imageData: ImageData): number {
  const data = imageData.data
  let total = 0
  const pixelCount = data.length / 4

  for (let i = 0; i < data.length; i += 4) {
    total += rgbToGrayscale(data[i], data[i + 1], data[i + 2])
  }

  return Math.round(total / pixelCount)
}

// ============================================
// 亮度采样
// ============================================

/**
 * 采样指定区域的平均亮度
 * @param imageData 图像数据
 * @param x 起始X坐标
 * @param y 起始Y坐标
 * @param width 采样宽度
 * @param height 采样高度
 */
export function sampleBrightness(
  imageData: ImageData,
  x: number,
  y: number,
  width: number,
  height: number
): number {
  const data = imageData.data
  const imgWidth = imageData.width
  let total = 0
  let count = 0

  const startX = Math.max(0, Math.floor(x))
  const startY = Math.max(0, Math.floor(y))
  const endX = Math.min(imageData.width, Math.floor(x + width))
  const endY = Math.min(imageData.height, Math.floor(y + height))

  for (let py = startY; py < endY; py++) {
    for (let px = startX; px < endX; px++) {
      const idx = (py * imgWidth + px) * 4
      total += rgbToGrayscale(data[idx], data[idx + 1], data[idx + 2])
      count++
    }
  }

  return count > 0 ? Math.round(total / count) : 0
}

/**
 * 采样指定区域的平均颜色
 * @param imageData 图像数据
 * @param x 起始X坐标
 * @param y 起始Y坐标
 * @param width 采样宽度
 * @param height 采样高度
 */
export function sampleColor(
  imageData: ImageData,
  x: number,
  y: number,
  width: number,
  height: number
): RGB {
  const data = imageData.data
  const imgWidth = imageData.width
  let r = 0, g = 0, b = 0
  let count = 0

  const startX = Math.max(0, Math.floor(x))
  const startY = Math.max(0, Math.floor(y))
  const endX = Math.min(imageData.width, Math.floor(x + width))
  const endY = Math.min(imageData.height, Math.floor(y + height))

  for (let py = startY; py < endY; py++) {
    for (let px = startX; px < endX; px++) {
      const idx = (py * imgWidth + px) * 4
      r += data[idx]
      g += data[idx + 1]
      b += data[idx + 2]
      count++
    }
  }

  return count > 0
    ? { r: Math.round(r / count), g: Math.round(g / count), b: Math.round(b / count) }
    : { r: 128, g: 128, b: 128 }
}

/**
 * 同时采样亮度和颜色
 */
export function samplePixel(
  imageData: ImageData,
  x: number,
  y: number,
  width: number,
  height: number
): SampledPixel {
  const data = imageData.data
  const imgWidth = imageData.width
  let r = 0, g = 0, b = 0
  let count = 0

  const startX = Math.max(0, Math.floor(x))
  const startY = Math.max(0, Math.floor(y))
  const endX = Math.min(imageData.width, Math.floor(x + width))
  const endY = Math.min(imageData.height, Math.floor(y + height))

  for (let py = startY; py < endY; py++) {
    for (let px = startX; px < endX; px++) {
      const idx = (py * imgWidth + px) * 4
      r += data[idx]
      g += data[idx + 1]
      b += data[idx + 2]
      count++
    }
  }

  const color = count > 0
    ? { r: Math.round(r / count), g: Math.round(g / count), b: Math.round(b / count) }
    : { r: 128, g: 128, b: 128 }

  const brightness = rgbToGrayscale(color.r, color.g, color.b)

  return { brightness, color }
}

// ============================================
// 对比度与亮度调整
// ============================================

/**
 * 应用对比度调整
 * @param value 原始值 (0-255)
 * @param contrast 对比度系数 (1为原始, >1增加对比, <1降低对比)
 */
export function applyContrast(value: number, contrast: number): number {
  // 将值归一化到 -128 到 127 范围
  const normalized = value - 128
  // 应用对比度
  const adjusted = normalized * contrast
  // 转回 0-255 范围
  return Math.max(0, Math.min(255, Math.round(adjusted + 128)))
}

/**
 * 应用亮度调整
 * @param value 原始值 (0-255)
 * @param brightness 亮度偏移 (-255 到 255)
 */
export function applyBrightness(value: number, brightness: number): number {
  return Math.max(0, Math.min(255, value + brightness))
}

/**
 * 同时应用对比度和亮度
 * @param value 原始值 (0-255)
 * @param contrast 对比度系数
 * @param brightness 亮度偏移
 */
export function applyContrastAndBrightness(
  value: number,
  contrast: number,
  brightness: number
): number {
  return applyBrightness(applyContrast(value, contrast), brightness)
}

/**
 * 反转亮度值
 * @param value 原始值 (0-255)
 */
export function invertBrightness(value: number): number {
  return 255 - value
}

// ============================================
// 颜色处理
// ============================================

/**
 * 应用颜色模式到RGB值
 * @param color 原始颜色
 * @param colorMode 颜色模式
 * @param customColor 自定义颜色 (当colorMode为'custom'时使用)
 */
export function applyColorMode(
  color: RGB,
  colorMode: 'grayscale' | 'color' | 'green' | 'amber' | 'custom',
  customColor?: string
): RGB {
  const brightness = rgbToGrayscale(color.r, color.g, color.b)

  switch (colorMode) {
    case 'grayscale':
      return { r: brightness, g: brightness, b: brightness }

    case 'color':
      return color

    case 'green':
      // 绿色终端风格
      return { r: 0, g: Math.round(brightness * 0.9), b: 0 }

    case 'amber':
      // 琥珀终端风格
      return { r: brightness, g: Math.round(brightness * 0.78), b: 0 }

    case 'custom':
      if (customColor) {
        const custom = hexToRGB(customColor)
        return {
          r: Math.round(custom.r * brightness / 255),
          g: Math.round(custom.g * brightness / 255),
          b: Math.round(custom.b * brightness / 255),
        }
      }
      return { r: brightness, g: brightness, b: brightness }

    default:
      return { r: brightness, g: brightness, b: brightness }
  }
}

// ============================================
// 颜色工具函数
// ============================================

/**
 * 十六进制颜色转RGB
 * @param hex 十六进制颜色 (如 '#00ff99' 或 '00ff99')
 */
export function hexToRGB(hex: string): RGB {
  const cleanHex = hex.replace('#', '')
  const r = parseInt(cleanHex.slice(0, 2), 16) || 0
  const g = parseInt(cleanHex.slice(2, 4), 16) || 0
  const b = parseInt(cleanHex.slice(4, 6), 16) || 0
  return { r, g, b }
}

/**
 * RGB转十六进制颜色
 */
export function rgbToHex(r: number, g: number, b: number): string {
  const toHex = (n: number) => Math.max(0, Math.min(255, n)).toString(16).padStart(2, '0')
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`
}

/**
 * RGB对象转CSS颜色字符串
 */
export function rgbToCSS(color: RGB): string {
  return `rgb(${color.r}, ${color.g}, ${color.b})`
}

/**
 * 计算两个颜色之间的线性插值
 * @param color1 起始颜色
 * @param color2 结束颜色
 * @param t 插值因子 (0-1)
 */
export function lerpColor(color1: RGB, color2: RGB, t: number): RGB {
  return {
    r: Math.round(color1.r + (color2.r - color1.r) * t),
    g: Math.round(color1.g + (color2.g - color1.g) * t),
    b: Math.round(color1.b + (color2.b - color1.b) * t),
  }
}

// ============================================
// 双色调处理
// ============================================

/** 双色调预设颜色 */
export const DUOTONE_PRESETS = {
  'amber-classic': {
    low: { r: 20, g: 12, b: 6 },
    high: { r: 255, g: 223, b: 178 },
  },
  'cyan-night': {
    low: { r: 6, g: 16, b: 22 },
    high: { r: 166, g: 240, b: 255 },
  },
  'violet-haze': {
    low: { r: 17, g: 10, b: 26 },
    high: { r: 242, g: 198, b: 255 },
  },
  'lime-pulse': {
    low: { r: 10, g: 18, b: 8 },
    high: { r: 226, g: 255, b: 162 },
  },
  'mono-ice': {
    low: { r: 12, g: 12, b: 12 },
    high: { r: 245, g: 248, b: 255 },
  },
} as const

/**
 * 应用双色调效果
 * @param brightness 亮度值 (0-255)
 * @param preset 双色调预设名称
 */
export function applyDuotone(
  brightness: number,
  preset: keyof typeof DUOTONE_PRESETS
): RGB {
  const { low, high } = DUOTONE_PRESETS[preset]
  const t = brightness / 255
  return lerpColor(low, high, t)
}

// ============================================
// 图像预处理
// ============================================

/**
 * 创建缩放后的图像Canvas
 * @param source 源图像
 * @param maxWidth 最大宽度
 * @param maxHeight 最大高度
 */
export function resizeImage(
  source: HTMLImageElement | HTMLCanvasElement | ImageData,
  maxWidth: number,
  maxHeight: number
): HTMLCanvasElement {
  let sourceWidth: number, sourceHeight: number
  let imageData: ImageData | null = null

  if (source instanceof ImageData) {
    sourceWidth = source.width
    sourceHeight = source.height
    imageData = source
  } else {
    sourceWidth = source.width
    sourceHeight = source.height
  }

  // 计算缩放比例
  const scaleX = maxWidth / sourceWidth
  const scaleY = maxHeight / sourceHeight
  const scale = Math.min(scaleX, scaleY, 1) // 不放大

  const targetWidth = Math.round(sourceWidth * scale)
  const targetHeight = Math.round(sourceHeight * scale)

  // 创建目标Canvas
  const canvas = document.createElement('canvas')
  canvas.width = targetWidth
  canvas.height = targetHeight
  const ctx = canvas.getContext('2d')!

  if (imageData) {
    // 从ImageData创建临时Canvas
    const tempCanvas = document.createElement('canvas')
    tempCanvas.width = sourceWidth
    tempCanvas.height = sourceHeight
    const tempCtx = tempCanvas.getContext('2d')!
    tempCtx.putImageData(imageData, 0, 0)
    ctx.drawImage(tempCanvas, 0, 0, targetWidth, targetHeight)
  } else if (source instanceof HTMLImageElement || source instanceof HTMLCanvasElement) {
    ctx.drawImage(source, 0, 0, targetWidth, targetHeight)
  }

  return canvas
}

/**
 * 从图像获取ImageData
 */
export function getImageData(
  source: HTMLImageElement | HTMLCanvasElement
): ImageData {
  const canvas = document.createElement('canvas')
  canvas.width = source.width
  canvas.height = source.height
  const ctx = canvas.getContext('2d')!
  ctx.drawImage(source, 0, 0)
  return ctx.getImageData(0, 0, canvas.width, canvas.height)
}

// ============================================
// Wave 8.3: 灰度预计算缓存
// ============================================

/**
 * 预计算的灰度缓存数据
 */
export interface GrayscaleCacheData {
  /** 灰度数组 (width * height) */
  grayscale: Float32Array
  /** 原始RGB数据 (width * height * 3) - 用于颜色采样 */
  rgbArray: Uint8Array
  /** 图像宽度 */
  width: number
  /** 图像高度 */
  height: number
  /** 对比度参数 */
  contrast: number
  /** 亮度参数 */
  brightness: number
  /** 缓存创建时间戳 */
  timestamp: number
}

/**
 * 灰度缓存管理类
 *
 * 管理预计算的灰度数组缓存，避免每帧重复计算
 * Wave 8.3: 性能优化
 */
export class GrayscaleCache {
  private cache: GrayscaleCacheData | null = null

  /**
   * 获取缓存数据
   */
  get(): GrayscaleCacheData | null {
    return this.cache
  }

  /**
   * 检查缓存是否有效
   */
  isValid(source: HTMLImageElement | HTMLCanvasElement | ImageData, contrast: number, brightness: number): boolean {
    if (!this.cache) return false

    // 检查参数是否匹配
    if (this.cache.contrast !== contrast || this.cache.brightness !== brightness) {
      return false
    }

    // 检查尺寸是否匹配
    let width: number, height: number
    if (source instanceof ImageData) {
      width = source.width
      height = source.height
    } else {
      width = source.width
      height = source.height
    }

    if (this.cache.width !== width || this.cache.height !== height) {
      return false
    }

    return true
  }

  /**
   * 创建或更新缓存
   *
   * @param source 源图像
   * @param contrast 对比度系数 (默认1)
   * @param brightness 亮度偏移 (默认0)
   */
  createOrUpdate(
    source: HTMLImageElement | HTMLCanvasElement | ImageData,
    contrast: number = 1,
    brightness: number = 0
  ): GrayscaleCacheData {
    // 检查是否需要更新
    if (this.isValid(source, contrast, brightness)) {
      return this.cache!
    }

    // 创建新缓存
    this.cache = precomputeGrayscaleArray(source, contrast, brightness)
    return this.cache
  }

  /**
   * 清除缓存
   */
  clear(): void {
    this.cache = null
  }

  /**
   * 检查是否有缓存
   */
  hasCache(): boolean {
    return this.cache !== null
  }
}

/**
 * 预计算灰度数组
 *
 * 一次性将整个图像转换为灰度数组，用于后续快速采样
 * 避免每帧重复计算灰度值
 *
 * @param source 源图像
 * @param contrast 对比度系数 (默认1)
 * @param brightness 亮度偏移 (默认0)
 * @returns 预计算的灰度缓存数据
 */
export function precomputeGrayscaleArray(
  source: HTMLImageElement | HTMLCanvasElement | ImageData,
  contrast: number = 1,
  brightness: number = 0
): GrayscaleCacheData {
  // 获取ImageData
  let imageData: ImageData
  if (source instanceof ImageData) {
    imageData = source
  } else {
    imageData = getImageData(source)
  }

  const { width, height, data } = imageData
  const grayscale = new Float32Array(width * height)
  const rgbArray = new Uint8Array(width * height * 3)

  for (let i = 0; i < width * height; i++) {
    const idx = i * 4
    const r = data[idx]
    const g = data[idx + 1]
    const b = data[idx + 2]

    // 存储RGB值
    rgbArray[i * 3] = r
    rgbArray[i * 3 + 1] = g
    rgbArray[i * 3 + 2] = b

    // 计算灰度值
    let gray = rgbToGrayscale(r, g, b)

    // 应用对比度
    if (contrast !== 1) {
      gray = Math.max(0, Math.min(255, Math.round((gray - 128) * contrast + 128)))
    }

    // 应用亮度
    if (brightness !== 0) {
      gray = Math.max(0, Math.min(255, gray + Math.round(brightness * 255)))
    }

    grayscale[i] = gray
  }

  return {
    grayscale,
    rgbArray,
    width,
    height,
    contrast,
    brightness,
    timestamp: performance.now()
  }
}

/**
 * 快速像素采样（从预计算灰度数组）
 *
 * 直接从灰度缓存数组读取，避免重复计算灰度值
 * 这是 Wave 8.3 的核心性能优化函数
 *
 * @param cache 预计算的灰度缓存
 * @param x 起始X坐标
 * @param y 起始Y坐标
 * @param width 采样宽度
 * @param height 采样高度
 * @returns 采样结果（亮度 + 颜色）
 */
export function samplePixelFast(
  cache: GrayscaleCacheData,
  x: number,
  y: number,
  width: number,
  height: number
): SampledPixel {
  const { grayscale, rgbArray, width: imgWidth, height: imgHeight } = cache

  const startX = Math.max(0, Math.floor(x))
  const startY = Math.max(0, Math.floor(y))
  const endX = Math.min(imgWidth, Math.floor(x + width))
  const endY = Math.min(imgHeight, Math.floor(y + height))

  let totalGray = 0
  let r = 0, g = 0, b = 0
  let count = 0

  // 直接从预计算数组读取，无需每帧计算
  for (let py = startY; py < endY; py++) {
    for (let px = startX; px < endX; px++) {
      const idx = py * imgWidth + px

      // 直接读取预计算的灰度值
      totalGray += grayscale[idx]

      // 读取RGB值
      const rgbIdx = idx * 3
      r += rgbArray[rgbIdx]
      g += rgbArray[rgbIdx + 1]
      b += rgbArray[rgbIdx + 2]

      count++
    }
  }

  const color = count > 0
    ? { r: Math.round(r / count), g: Math.round(g / count), b: Math.round(b / count) }
    : { r: 128, g: 128, b: 128 }

  const brightness = count > 0 ? Math.round(totalGray / count) : 128

  return { brightness, color }
}

/**
 * 快速亮度采样（仅获取亮度，不含颜色）
 *
 * 最快的采样方式，仅需要亮度值时使用
 *
 * @param cache 预计算的灰度缓存
 * @param x 起始X坐标
 * @param y 起始Y坐标
 * @param width 采样宽度
 * @param height 采样高度
 * @returns 亮度值 (0-255)
 */
export function sampleBrightnessFast(
  cache: GrayscaleCacheData,
  x: number,
  y: number,
  width: number,
  height: number
): number {
  const { grayscale, width: imgWidth, height: imgHeight } = cache

  const startX = Math.max(0, Math.floor(x))
  const startY = Math.max(0, Math.floor(y))
  const endX = Math.min(imgWidth, Math.floor(x + width))
  const endY = Math.min(imgHeight, Math.floor(y + height))

  let total = 0
  let count = 0

  for (let py = startY; py < endY; py++) {
    for (let px = startX; px < endX; px++) {
      const idx = py * imgWidth + px
      total += grayscale[idx]
      count++
    }
  }

  return count > 0 ? Math.round(total / count) : 0
}

/**
 * 快速颜色采样（仅获取颜色，不含亮度）
 *
 * @param cache 预计算的灰度缓存
 * @param x 起始X坐标
 * @param y 起始Y坐标
 * @param width 采样宽度
 * @param height 采样高度
 * @returns RGB颜色
 */
export function sampleColorFast(
  cache: GrayscaleCacheData,
  x: number,
  y: number,
  width: number,
  height: number
): RGB {
  const { rgbArray, width: imgWidth, height: imgHeight } = cache

  const startX = Math.max(0, Math.floor(x))
  const startY = Math.max(0, Math.floor(y))
  const endX = Math.min(imgWidth, Math.floor(x + width))
  const endY = Math.min(imgHeight, Math.floor(y + height))

  let r = 0, g = 0, b = 0
  let count = 0

  for (let py = startY; py < endY; py++) {
    for (let px = startX; px < endX; px++) {
      const idx = py * imgWidth + px
      const rgbIdx = idx * 3
      r += rgbArray[rgbIdx]
      g += rgbArray[rgbIdx + 1]
      b += rgbArray[rgbIdx + 2]
      count++
    }
  }

  return count > 0
    ? { r: Math.round(r / count), g: Math.round(g / count), b: Math.round(b / count) }
    : { r: 128, g: 128, b: 128 }
}

/**
 * 快速单点灰度值获取
 *
 * 获取指定像素位置的灰度值（不进行区域平均）
 *
 * @param cache 预计算的灰度缓存
 * @param x X坐标
 * @param y Y坐标
 * @returns 灰度值 (0-255)
 */
export function getPixelGrayscaleFast(
  cache: GrayscaleCacheData,
  x: number,
  y: number
): number {
  const { grayscale, width: imgWidth, height: imgHeight } = cache

  const px = Math.floor(x)
  const py = Math.floor(y)

  if (px < 0 || py < 0 || px >= imgWidth || py >= imgHeight) {
    return 0
  }

  return grayscale[py * imgWidth + px]
}

/**
 * 快速单点RGB值获取
 *
 * 获取指定像素位置的RGB值
 *
 * @param cache 预计算的灰度缓存
 * @param x X坐标
 * @param y Y坐标
 * @returns RGB颜色
 */
export function getPixelRGBFast(
  cache: GrayscaleCacheData,
  x: number,
  y: number
): RGB {
  const { rgbArray, width: imgWidth, height: imgHeight } = cache

  const px = Math.floor(x)
  const py = Math.floor(y)

  if (px < 0 || py < 0 || px >= imgWidth || py >= imgHeight) {
    return { r: 0, g: 0, b: 0 }
  }

  const idx = py * imgWidth + px
  return {
    r: rgbArray[idx * 3],
    g: rgbArray[idx * 3 + 1],
    b: rgbArray[idx * 3 + 2]
  }
}

// 导出全局灰度缓存实例（供 Renderer 使用）
export const globalGrayscaleCache = new GrayscaleCache()